import { useEffect, useRef, useState } from 'react';
import { Copy, ChevronsRight, Trash2, Palette, Check, X } from 'lucide-react';

// Şub 2026 — Hücre renk paleti. Backend hex olarak saklar; null=varsayılan.
const CELL_COLOR_PALETTE = [
  { name: 'Zümrüt', hex: '#10B981' },
  { name: 'Turkuaz', hex: '#14B8A6' },
  { name: 'Mavi', hex: '#3B82F6' },
  { name: 'İndigo', hex: '#6366F1' },
  { name: 'Mor', hex: '#8B5CF6' },
  { name: 'Pembe', hex: '#EC4899' },
  { name: 'Kırmızı', hex: '#EF4444' },
  { name: 'Turuncu', hex: '#F97316' },
  { name: 'Sarı', hex: '#F59E0B' },
  { name: 'Gri', hex: '#64748B' },
];

/**
 * Right-click context menu for an assignment cell.
 *
 * Positions itself at (x, y) — pinned within the viewport. Closes on outside
 * click, Escape, or scroll. The parent (Planning.jsx) keeps `ctxMenu` state
 * with { x, y, assignment, route } and renders this when truthy.
 */
export function CellContextMenu({ x, y, assignment, route, onSpreadEmployee, onCopyToNext, onRemove, onSetColor, onClose, weekDates }) {
  const ref = useRef(null);
  const [colorOpen, setColorOpen] = useState(false);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  // Clamp position so the menu doesn't render off-screen.
  const W = 240, H = colorOpen ? 260 : 180;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top  = Math.min(y, window.innerHeight - H - 8);

  const srcDate = assignment.assignment_date;
  const idx = (weekDates || []).indexOf(srcDate);
  const canSpread = idx >= 0 && idx < (weekDates?.length || 7) - 1;
  const canCopyNext = canSpread; // same condition
  const currentColor = assignment.cell_color || null;

  const applyColor = (hex) => {
    if (typeof onSetColor === 'function') onSetColor(hex);
    onClose();
  };

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', left, top, width: W }}
      className="z-[70] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-xl py-1 text-sm"
      data-testid="planning-cell-context-menu"
    >
      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 truncate flex items-center gap-1">
        {currentColor && (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full border border-slate-300"
            style={{ backgroundColor: currentColor }}
            title="Aktif renk"
          />
        )}
        {assignment.employee_name} · {route.route_code}
      </div>
      {canCopyNext && (
        <button
          onClick={() => { onCopyToNext(); onClose(); }}
          className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
          data-testid="planning-ctx-copy-next"
        >
          <Copy className="h-3.5 w-3.5 text-emerald-500" /> Sonraki güne kopyala
        </button>
      )}
      {canSpread && (
        <button
          onClick={() => { onSpreadEmployee(); onClose(); }}
          className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
          data-testid="planning-ctx-spread"
        >
          <ChevronsRight className="h-3.5 w-3.5 text-indigo-500" /> Bu kişi+rotayı kalan haftaya yay
        </button>
      )}
      {typeof onSetColor === 'function' && (
        <>
          <div className="border-t border-slate-100 dark:border-slate-700 my-0.5" />
          <button
            onClick={() => setColorOpen(v => !v)}
            className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-purple-50 dark:hover:bg-purple-900/20"
            data-testid="planning-ctx-color"
          >
            <Palette className="h-3.5 w-3.5 text-purple-500" />
            <span className="flex-1">Hücre rengi</span>
            <span
              className="inline-block h-4 w-4 rounded border border-slate-300"
              style={{ backgroundColor: currentColor || 'transparent', backgroundImage: currentColor ? 'none' : 'repeating-linear-gradient(45deg, #e5e7eb 0 4px, transparent 4px 8px)' }}
            />
          </button>
          {colorOpen && (
            <div className="px-3 py-2 border-t border-b border-slate-100 dark:border-slate-700 space-y-2" data-testid="planning-ctx-color-panel">
              <div className="grid grid-cols-5 gap-1.5">
                {CELL_COLOR_PALETTE.map(c => {
                  const active = (currentColor || '').toUpperCase() === c.hex.toUpperCase();
                  return (
                    <button
                      key={c.hex}
                      title={c.name}
                      onClick={() => applyColor(c.hex)}
                      className={`h-6 w-6 rounded flex items-center justify-center border ${active ? 'ring-2 ring-offset-1 ring-slate-800 dark:ring-white border-transparent' : 'border-slate-200 dark:border-slate-600'} hover:scale-110 transition`}
                      style={{ backgroundColor: c.hex }}
                      data-testid={`planning-ctx-color-${c.hex.replace('#','')}`}
                    >
                      {active && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}
                    </button>
                  );
                })}
              </div>
              {currentColor && (
                <button
                  onClick={() => applyColor(null)}
                  className="w-full flex items-center justify-center gap-1 text-[11px] text-slate-500 hover:text-red-600 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="planning-ctx-color-clear"
                >
                  <X className="h-3 w-3" /> Rengi kaldır
                </button>
              )}
            </div>
          )}
        </>
      )}
      <div className="border-t border-slate-100 dark:border-slate-700 my-0.5" />
      <button
        onClick={() => { onRemove(); onClose(); }}
        className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
        data-testid="planning-ctx-remove"
      >
        <Trash2 className="h-3.5 w-3.5" /> Atamayı sil
      </button>
    </div>
  );
}
